/**
 * API client for the Wholesale Workspace.
 * Wraps /wholesale/* endpoints with typed fetchers. Mirrors backend Pydantic
 * shapes — field names match server JSON keys with no conversion layer.
 */
import { API } from "../config/api"

// ---------------------------------------------------------------------------
// Primitive unions
// ---------------------------------------------------------------------------

export type WholesaleVendor = "adesa" | "tekion"

export type WholesaleTab = "cleared" | "not_cleared" | "unmatched_adesa" | "excluded"

// Per-row match state, drives the Status column.
export type WholesaleMatchState =
  | "matched"          // clean VIN+stock match (green)
  | "user_selected"    // user picked a candidate (blue)
  | "needs_selection"  // awaiting a pick (validator only)
  | "unmatched"        // no Tekion match (dash)
  | "excluded"         // user kept it out of the cleared list

export type WholesaleRunStatus = "draft" | "completed" | "failed"

// ---------------------------------------------------------------------------
// Files / session
// ---------------------------------------------------------------------------

export interface WholesaleFileSummary {
  file_id: number
  vendor: WholesaleVendor
  filename: string
  row_count: number
  uploaded_at: string | null
}

export interface WholesaleSession {
  adesa: WholesaleFileSummary | null
  tekion: WholesaleFileSummary | null
}

// ---------------------------------------------------------------------------
// Vehicles + duplicates
// ---------------------------------------------------------------------------

export interface WholesaleVehicle {
  id: number | null
  // "needs_selection" rows carry the sentinel tab; use match_state to route.
  tab: WholesaleTab | "needs_selection"
  match_state: WholesaleMatchState | null
  adesa_row_order: number
  // Adesa side
  client: string | null
  adesa_stock: string | null
  age_date: string | null
  vin: string | null
  year: number | null
  make: string | null
  model: string | null
  color: string | null
  odo: number | null
  store: string | null
  notes: string | null
  // Tekion side — null on unmatched_adesa rows
  tekion_stock: string | null
  tekion_vin: string | null
  tekion_status: string | null
  tekion_year_make_model: string | null
  tekion_model_number: string | null
  tekion_mileage: number | null
  tekion_exterior_color: string | null
  tekion_age_days: number | null
  tekion_stock_type: string | null
  tekion_year: number | null
  tekion_make: string | null
  tekion_model: string | null
  tekion_stocked_in_date: string | null
  tekion_title_status: string | null
  // Present on needs_selection / user_selected rows so the validator can
  // (re-)pick; null on clean matches and unmatched rows.
  match_candidates: WholesaleTekionCandidate[] | null
  suggested_tekion_stock: string | null
}

export interface WholesaleTekionCandidate {
  tekion_stock: string | null
  tekion_vin: string | null
  tekion_status: string | null
  tekion_year_make_model: string | null
  tekion_model_number: string | null
  tekion_mileage: number | null
  tekion_exterior_color: string | null
  tekion_age_days: number | null
  tekion_stock_type: string | null
  tekion_year: number | null
  tekion_make: string | null
  tekion_model: string | null
  tekion_stocked_in_date: string | null
  tekion_title_status: string | null
}

// Body for creating a draft and for re-applying the families filter.
export interface WholesaleFilterRequest {
  families: string[]
  per_value?: Record<string, boolean> | null
}

// ---------------------------------------------------------------------------
// Runs / reports
// ---------------------------------------------------------------------------

export interface WholesaleRunSummary {
  id: number
  started_at: string
  completed_at: string | null
  status: WholesaleRunStatus
  initiated_by_username: string | null
  adesa_filename: string | null
  adesa_row_count: number | null
  tekion_filename: string | null
  tekion_row_count: number | null
  cleared_count: number
  not_cleared_count: number
  unmatched_adesa_count: number
  applied_families: string[]
  applied_per_value: Record<string, boolean> | null
  duplicate_decisions: Record<string, string> | null
  error_message: string | null
}

export interface WholesaleRunListResponse {
  runs: WholesaleRunSummary[]
}

export interface WholesaleRunDetail {
  run: WholesaleRunSummary
  vehicles: WholesaleVehicle[]
}

// The flat vehicle list grouped for display: the three results tabs plus
// the needs_selection rows (which live in the validator, not a tab).
export interface WholesaleVehicleGroups {
  cleared: WholesaleVehicle[]
  not_cleared: WholesaleVehicle[]
  unmatched_adesa: WholesaleVehicle[]
  excluded: WholesaleVehicle[]
  needs_selection: WholesaleVehicle[]
}

export function groupVehicles(
  detail: WholesaleRunDetail | null,
): WholesaleVehicleGroups | null {
  if (!detail) return null
  const v = detail.vehicles
  return {
    cleared: v.filter((x) => x.tab === "cleared"),
    not_cleared: v.filter((x) => x.tab === "not_cleared"),
    unmatched_adesa: v.filter((x) => x.tab === "unmatched_adesa"),
    excluded: v.filter((x) => x.tab === "excluded"),
    needs_selection: v.filter((x) => x.match_state === "needs_selection"),
  }
}

// ---------------------------------------------------------------------------
// Cleared families
// ---------------------------------------------------------------------------

export interface WholesaleClearedFamily {
  id: number
  family: string
  created_at: string
  created_by_username: string | null
}

export interface WholesaleClearedFamiliesResponse {
  families: WholesaleClearedFamily[]
}

// ---------------------------------------------------------------------------
// Auth helper (copied-in-shape from photoList.ts; consolidation is a
// project-wide cleanup item flagged in frontend-standards §7).
// ---------------------------------------------------------------------------

const TOKEN_KEY = "invi_token"

function getToken(): string {
  return localStorage.getItem(TOKEN_KEY) ?? ""
}

async function authFetch(url: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(url, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
      ...(init?.headers ?? {}),
    },
  })
  if (!res.ok) {
    let detail = ""
    try {
      const body = await res.json()
      if (body && typeof body.detail === "string") {
        detail = body.detail
      }
    } catch {
      // fall through to text
    }
    if (!detail) {
      try {
        detail = await res.text()
      } catch {
        // fall through to status
      }
    }
    throw new Error(detail || `HTTP ${res.status}`)
  }
  return res
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

const E = API.endpoints.wholesale

export async function getSession(): Promise<WholesaleSession> {
  const res = await authFetch(`${API.baseUrl}${E.session}`)
  return res.json()
}

export async function uploadFile(
  file: File,
  overrideVendor?: WholesaleVendor,
): Promise<WholesaleFileSummary> {
  const formData = new FormData()
  formData.append("file", file)
  if (overrideVendor) formData.append("override_vendor", overrideVendor)

  const res = await fetch(`${API.baseUrl}${E.upload}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${getToken()}` },
    body: formData,
  })
  if (!res.ok) {
    let detail = ""
    try {
      const body = await res.json()
      if (body && typeof body.detail === "string") detail = body.detail
    } catch {
      // fall through
    }
    if (!detail) detail = (await res.text()) || `HTTP ${res.status}`
    throw new Error(detail)
  }
  return res.json()
}

export async function reassignVendor(
  fileId: number,
  vendor: WholesaleVendor,
): Promise<WholesaleFileSummary> {
  const res = await authFetch(`${API.baseUrl}${E.reassign(fileId)}`, {
    method: "POST",
    body: JSON.stringify({ vendor }),
  })
  return res.json()
}

export async function deleteFile(fileId: number): Promise<void> {
  await authFetch(`${API.baseUrl}${E.deleteFile(fileId)}`, {
    method: "DELETE",
  })
}

// Create a draft run from the user's uploaded files (no "Run Report" step).
export async function createDraft(
  request: WholesaleFilterRequest,
  dealerGroupId?: number | null,
): Promise<WholesaleRunDetail> {
  const qs = dealerGroupId != null ? `?dealer_group_id=${dealerGroupId}` : ""
  const res = await authFetch(`${API.baseUrl}${E.draft}${qs}`, {
    method: "POST",
    body: JSON.stringify(request),
  })
  return res.json()
}

// The user's most recent draft in the active group (null when none).
export async function getLatestDraft(
  dealerGroupId?: number | null,
): Promise<WholesaleRunDetail | null> {
  const qs = dealerGroupId != null ? `?dealer_group_id=${dealerGroupId}` : ""
  const res = await authFetch(`${API.baseUrl}${E.draftLatest}${qs}`)
  const text = await res.text()
  return text ? (JSON.parse(text) as WholesaleRunDetail) : null
}

// Re-apply the families filter to a draft (live re-bucketing).
export async function rebucketDraft(
  runId: number,
  request: WholesaleFilterRequest,
): Promise<WholesaleRunDetail> {
  const res = await authFetch(`${API.baseUrl}${E.rebucket(runId)}`, {
    method: "POST",
    body: JSON.stringify(request),
  })
  return res.json()
}

// Resolve a needs_selection vehicle by picking one of its candidates.
export async function selectCandidate(
  runId: number,
  vehicleId: number,
  tekionStock: string,
): Promise<WholesaleRunDetail> {
  const res = await authFetch(`${API.baseUrl}${E.selectCandidate(runId, vehicleId)}`, {
    method: "POST",
    body: JSON.stringify({ tekion_stock: tekionStock }),
  })
  return res.json()
}

// Keep a validator vehicle out of the cleared list (-> Excluded tab).
export async function excludeVehicle(
  runId: number,
  vehicleId: number,
): Promise<WholesaleRunDetail> {
  const res = await authFetch(`${API.baseUrl}${E.excludeVehicle(runId, vehicleId)}`, {
    method: "POST",
  })
  return res.json()
}

// Undo an exclude — return the vehicle to the validator.
export async function restoreVehicle(
  runId: number,
  vehicleId: number,
): Promise<WholesaleRunDetail> {
  const res = await authFetch(`${API.baseUrl}${E.restoreVehicle(runId, vehicleId)}`, {
    method: "POST",
  })
  return res.json()
}

export async function completeRun(runId: number): Promise<WholesaleRunSummary> {
  const res = await authFetch(`${API.baseUrl}${E.complete(runId)}`, { method: "POST" })
  return res.json()
}

export async function reopenRun(runId: number): Promise<WholesaleRunSummary> {
  const res = await authFetch(`${API.baseUrl}${E.reopen(runId)}`, { method: "POST" })
  return res.json()
}

export async function listRuns(
  limit = 50,
  dealerGroupId?: number | null,
): Promise<WholesaleRunListResponse> {
  const qs = dealerGroupId != null ? `&dealer_group_id=${dealerGroupId}` : ""
  const res = await authFetch(`${API.baseUrl}${E.runs}?limit=${limit}${qs}`)
  return res.json()
}

export async function getRun(runId: number): Promise<WholesaleRunDetail> {
  const res = await authFetch(`${API.baseUrl}${E.runById(runId)}`)
  return res.json()
}

export async function listClearedFamilies(
  dealerGroupId?: number | null,
): Promise<WholesaleClearedFamiliesResponse> {
  const qs = dealerGroupId != null ? `?dealer_group_id=${dealerGroupId}` : ""
  const res = await authFetch(`${API.baseUrl}${E.clearedFamilies}${qs}`)
  return res.json()
}

export async function addClearedFamily(
  family: string,
  dealerGroupId?: number | null,
): Promise<WholesaleClearedFamily> {
  const qs = dealerGroupId != null ? `?dealer_group_id=${dealerGroupId}` : ""
  const res = await authFetch(`${API.baseUrl}${E.clearedFamilies}${qs}`, {
    method: "POST",
    body: JSON.stringify({ family }),
  })
  return res.json()
}

export async function removeClearedFamily(
  family: string,
  dealerGroupId?: number | null,
): Promise<void> {
  const qs = dealerGroupId != null ? `?dealer_group_id=${dealerGroupId}` : ""
  await authFetch(`${API.baseUrl}${E.clearedFamilyByName(family)}${qs}`, {
    method: "DELETE",
  })
}

// ---------------------------------------------------------------------------
// XLSX export — Blob fetchers (the response body is the workbook file).
// ---------------------------------------------------------------------------

export async function exportWholesaleRunTab(
  runId: number,
  tab: WholesaleTab,
): Promise<Blob> {
  const url = `${API.baseUrl}${E.runById(runId)}/export?tab=${encodeURIComponent(tab)}`
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${getToken()}` },
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(body || `Export failed: ${res.status}`)
  }
  return res.blob()
}

export async function exportWholesaleRunFull(runId: number): Promise<Blob> {
  const url = `${API.baseUrl}${E.runById(runId)}/export-full`
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${getToken()}` },
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(body || `Export failed: ${res.status}`)
  }
  return res.blob()
}
